import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, DataGridRef, Editing, GroupPanel, Item as GridToolbarItem, Lookup, Paging, Scrolling, Selection, Toolbar as GridToolbar } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { EmptyItem, FormRef, GroupItem, Item as FormItem, Label, SimpleItem, Tab, TabbedItem, TabPanelOptions } from "devextreme-react/form";
import { Item } from "devextreme-react/toolbar";
import IClient from "../../models/IClient";
import { FormListBase, FormListBaseGrid, FormListBaseTab, FormListBaseToolbar } from '../base';
import { useDateInput, useTelephoneInput } from "../../components/inputs";
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import { usePays, useProvinces, useSexe } from "../../components/selectbox";
import 'devextreme-react/text-area';
import useUsager from "../../components/selectbox/usager";
import Guid from "../../models/Guid";
import useApi from '../../hooks/useApi';
import useReservationCouleur from '../../components/selectbox/ReservationCouleur';
import useLookUpBase from "../../components/selectbox/LookupBase";
import FormSysteme from '../../components/forms/form-systeme';
import PopupPage, { useContextPopupPage } from "../../components/popups/PopupPage";
import GestionCategoriesClients from '../gestion-categories-clients/gestion-categories-clients';
import GestionTypesSejours from '../gestion-types-sejours/gestion-types-sejours';
import GestionLieuxSejour from '../gestion-lieux-sejour/gestion-lieux-sejour';
import GestionPublicite from '../gestion-publicite/gestion-publicite';
import GestionEntreprises from '../gestion-entreprises/gestion-entreprises';
import GestionPlans from '../gestion-plans/gestion-plans';
import PopupPhoto from './popup-photo';
import PopupFicheClient from './popup-fiche-client';
import { FocusedRowChangedEvent, InitNewRowEvent, RowRemovingEvent } from "devextreme/ui/data_grid";
import IClientNote from '../../models/IClientNote';
import GestionLangues from '../gestion-langues/gestion-langues';
import ConfigurationClientsTitres from '../configuration/configuration-clients-titres';
import IGestionClient from "../../models/IGestionClient";
import IClientPlaque from "../../models/IClientPlaque";
import IClientPermis from "../../models/IClientPermis";


interface IGestionClientProps {
    defaultSearch?: string
    isModeSelection?: boolean
    /** When true with isModeSelection, list+toolbar+detail stay visible (e.g. client picker popup where users edit before Sélectionner). */
    allowEditInSelectionPopup?: boolean
    saveBtnRef?: any
}

export default function GestionClient({isModeSelection, defaultSearch, saveBtnRef, allowEditInSelectionPopup}: IGestionClientProps) {
    const {ProvinceSelectBox, provincesEditorOptions, currentProvince, provinces} = useProvinces();
    const {PaysSelectBox} = usePays(true);
    const clientDataSourceOption: IFormBaseDataSourceOptions = useMemo(() => ({path: "client", primaryKey: "idClient", customReadByIdPath: "client/readDossier/{key}", customSauvegarderPath: "client/save", includeInactives: false, listColumnsFieldsname: ["nom", "prenom", "adresse", "actif", "id_province", "ville", "code_postal", "telephone1", "id_categorie_client"], isModeSelection: isModeSelection}), [isModeSelection]);
    const clientFormDataSource = useCustomDataSource<IClient, IGestionClient>(clientDataSourceOption);
    const { selectedDataRow: selectedClient, mode, setMode, setSelectedDataRow, isFieldModifier, setIsFieldModifier, setMainForm, sauvegarder } = clientFormDataSource;
    const { editorOptions: categoriesClientEditorOption, PopupGestion: PopupGestionCategoriesClient, data: categoriesClient } = useLookUpBase({
        isModeModifier: mode !== Mode.Lecture, controllerName: 'categorieclient',
        onClearRow: () => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idCategorieClient", Guid.empty.toString());
        },
        showClear: mode !== Mode.Lecture, titre: "Gestion des catégories de clients"
    });
    const { editorOptions: clientTitreEditorOption, PopupGestion: PopupGestionClientTitre } = useLookUpBase({
        isModeModifier: mode !== Mode.Lecture, controllerName: 'clienttitre',
        onClearRow: () => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idClientTitre", Guid.empty.toString());
        },
        showClear: mode !== Mode.Lecture, titre: "Gestion d'un titre"
    });
    const { editorOptions: languesEditorOption, PopupGestion: PopupGestionLangues } = useLookUpBase({
        isModeModifier: mode !== Mode.Lecture, controllerName: 'langue',
        onClearRow: () => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idLangue", Guid.empty.toString());
        },
        showClear: mode !== Mode.Lecture, titre: "Gestion d'une langue"
    });
    const { editorOptions: publicitesEditorOption, PopupGestion: PopupGestionPublicite } = useLookUpBase({
        isModeModifier: mode !== Mode.Lecture, controllerName: 'publicite',
        onClearRow: () => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idPublicite", Guid.empty.toString());
        },
        showClear: mode !== Mode.Lecture, titre: "Gestion d'une publicité"
    });
    const { editorOptions: typeSejourEditorOption, PopupGestion: PopupGestionTypeSejour } = useLookUpBase({
        isModeModifier: mode !== Mode.Lecture, controllerName: 'typesejours',
        onClearRow: () => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idTypeSejour", Guid.empty.toString());
        },
        showClear: mode !== Mode.Lecture, titre: "Gestionnaire des types de séjour"
    });
    const { editorOptions: lieuSejourEditorOption, PopupGestion: PopupGestionLieuSejour } = useLookUpBase({
        isModeModifier: mode !== Mode.Lecture, controllerName: 'lieusejour',
        onClearRow: () => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idLieuSejour", Guid.empty.toString());
        },
        showClear: mode !== Mode.Lecture, titre: "Gestionnaire des lieux de séjour"
    });
    const { editorOptions: permisTypeEditorOptions } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "permistype", showClear: mode !== Mode.Lecture });
    const { editorOptions: planEnteteEditorOptions } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "planentete", showClear: mode !== Mode.Lecture });
    const { editorOptions: produitEditorOptions } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "catalogue", showClear: mode !== Mode.Lecture });
    const { editorOptions: typePaiementEditorOptions } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "typepaiement", showClear: mode !== Mode.Lecture });
    const { editorOptions: entrepriseEditorOption, PopupGestion: PopupGestionEntreprise } = useLookUpBase({
        isModeModifier: mode !== Mode.Lecture, controllerName: 'entreprise',
        onClearRow: () => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idEntreprise", Guid.empty.toString());
        },
        showClear: mode !== Mode.Lecture
    });
    const { editorOptions: planParDefautEditorOption, PopupGestion: PopupGestionPlan } = useLookUpBase({
        isModeModifier: mode !== Mode.Lecture, controllerName: 'planentete',
        onClearRow: () => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idPlanEntete", Guid.empty.toString());
        },
        showClear: mode !== Mode.Lecture
    });
    const [isPopupVisiblePhoto, setPopupVisibilityPhoto] = useState(false);
    const [ficheClientActive, setFicheClientActive] = useState(false);
    const { SexeSelectBox } = useSexe();
    const { reservationCouleurGridCellItemRender } = useReservationCouleur();
    const { getData } = useApi<object>();
    const { dateOptions } = useDateInput();
    const { telephoneOptions } = useTelephoneInput();
    const clientPlaqueDataGridRef = useRef<DataGridRef>(null);
    const clientPermisDataGridRef = useRef<DataGridRef>(null);
    useImperativeHandle(saveBtnRef, () => ({
        sauvegarder(e: any) {
            defaultSearch = "";
            if (mode !== Mode.Lecture)
                return sauvegarder && sauvegarder(e)

            return null;
        },
    }))

    const allergiesDataSourceOptions = useMemo<IFormBaseDataSourceOptions>(() => {
        return { path: "clientNote", primaryKey: "idClientNote" }
    }, [selectedClient?.idClient]);
    const allergiesDataSource = useCustomDataSource<IClientNote, IClientNote>(allergiesDataSourceOptions);

    const { UsagerSelectBox } = useUsager();
    const { selectedValue, setSelectedValue } = useContextPopupPage();
    //my state
    const [DataReservation, setDataReservation] = useState<any[]>([]);
    const [DataFature, setDataFature] = useState<any[]>([]);
    const [DataPaiement, setDataPaiement] = useState<any[]>([]);
    const [DataBonsDeTavail, setDataBonsDeTravail] = useState<any[]>([]);
    const [DataEstimations, setDataEstimations] = useState<any[]>([]);
    const [DataContrats, setDataContrats] = useState<any[]>([]);
    const [DataRemisage, setDataRemisage] = useState<any[]>([]);
    const formRef = useRef<FormRef>(null);
    const dataGridRef = useRef<DataGridRef>(null);
    const etatCompteOptions = useMemo(() => {
        return {
            dataSource: ["Courriel", "Fax", "Poste"],
            layout: "horizontal"
        }
    }, []);

    useEffect(() => {
        formRef?.current?.instance().getEditor("nom")?.focus();
    }, [mode])

    const nomOptions = useMemo(() => {
        return { maxLength: 40 };
    }, []);

    const codeClientOptions = useMemo(() => {
        return { maxLength: 30 };
    }, []);

    const passwordOptions = useMemo(() => {
        return { maxLength: 150, mode: 'password' };
    }, []);

    const adresseOptions = useMemo(() => {
        return { maxLength: 100 };
    }, []);

    const codePostalOptions = useMemo(() => {
        return {
            mask: currentProvince?.residentCanada ? 'A0A 0A0' : '',
            maskRules: {
                A: /[A-Za-z]/,
                0: /[0-9]/,
            },
            maxLength: 9,
            // onValueChanged: (e: any) => {
            //   e.component.option("value", e.value?.toUpperCase());
            // },
        };
    }, [currentProvince?.residentCanada]);

    const noChasseurOptions = useMemo(() => {
        return { maxLength: 50 };
    }, []);

    const carteCreditOptions = useMemo(() => {
        return {
            maxLength: 140,
            text: selectedClient.carteCredit
                ? '************' + selectedClient.carteCredit.toString().substr(-4)
                : ''
        };
    }, [selectedClient.carteCredit]);

    const carteCreditExpOptions = useMemo(() => {
        return { maxLength: 112 };
    }, []);


    const emailOptions = useMemo(() => {
        return { maxLength: 255 };
    }, []);

    useEffect(() => {
        if (formRef)
            setMainForm(formRef.current?.instance());
    })

    useEffect(() => {

        if (selectedClient.idClient && mode !== Mode.Ajout && !selectedClient.idClient.isEmpty()) {
            _loadData(selectedClient?.idClient)
        }

        if (mode !== Mode.Lecture)
            formRef.current?.instance().getEditor("nom")?.focus();
    }, [selectedClient?.idClient, mode])

    const _loadReservation = useCallback(async (IDclient: Guid) => {
        const resultReservation = await getData({ customPath: `client/${IDclient}/reservationsHistorique` });
        const resultArray = Object.values(resultReservation);
        //cree le nouveau tableau de donnees
        const modifiedDetailHistorique: any[] = []
        resultArray.map(reservation => {
            //verifie si le tableau reservationDetailHistorique est vide
            if (reservation.reservationDetailHistorique.length < 1) {
                const { reservationDetailHistorique, ...nouvelObjet } = reservation;
                modifiedDetailHistorique.push({ ...nouvelObjet })
            }
            // le tableau reservationDetailHistorique n'est pas vide
            else {
                const { annee, reservationDetailHistorique, noConfirmation, momtant } = reservation;
                // parcour tout les details de reservations
                reservationDetailHistorique.map((detail: any) => {
                    //copie chaque objet de details dans modifiedDetail
                    const modifiedDetail = { ...detail };
                    //ajoute les deux proprietes ci-dessous a l'objet copie
                    modifiedDetail.annee = annee;
                    modifiedDetail.montantEntee = momtant;
                    modifiedDetail.noConfirmation = noConfirmation;
                    //ajoute cet objet dans le tableau final
                    modifiedDetailHistorique.push(modifiedDetail);
                });
            }
        });
        setDataReservation(modifiedDetailHistorique as any[]);
    }, [getData]);

    const _loadFacture = useCallback(async (IDclient: Guid) => {
        const resultFactures = await getData({ customPath: `client/${IDclient}/facturesHistorique` });
        const resultArray = Object.values(resultFactures);
        //cree le nouveau tableau de donnees
        const modifiedDetailHistorique: any[] = []
        resultArray.map(facture => {
            //verifie si le tableau facturationDetailHistorique est vide
            if (facture.facturationDetailHistorique.length < 1) {
                const { reservationDetailHistorique, ...nouvelObjet } = facture;
                const modifiedDetail = { ...nouvelObjet };
                const date = new Date(nouvelObjet?.dateDeVente);
                const year = date.getFullYear();
                modifiedDetail.noFacture = nouvelObjet?.noFacture + " \tDate :" + year + "\tMontant : " + nouvelObjet?.montantFacture + "\tSolde :" + nouvelObjet?.solde
                //ajoute cet objet dans le tableau final
                modifiedDetailHistorique.push(modifiedDetail)
            }
            // le tableau reservationDetailHistorique n'est pas vide
            else {
                const { annee, facturationDetailHistorique, noFacture, solde, montantFacture, dateDeVente } = facture;
                // parcour tout les details de reservations
                facturationDetailHistorique.map((detail: any) => {
                    //copie chaque objet de details dans modifiedDetail
                    const modifiedDetail = { ...detail };
                    const date = new Date(dateDeVente);
                    const year = date.getFullYear();
                    modifiedDetail.annee = annee;
                    modifiedDetail.soldetotal = solde;
                    // modifiedDetail.noFacture = noFacture;
                    modifiedDetail.noFacture = noFacture + " \tDate : " + year + "\tMontant : " + montantFacture + " \tSolde :" + solde

                    //ajoute cet objet dans le tableau final
                    modifiedDetailHistorique.push(modifiedDetail);
                });
            }
        });
        setDataFature(modifiedDetailHistorique as any[]);
    }, [getData]);

    const _loadVehicules = useCallback(async (IDclient: Guid) => {
        const results = await getData({ customPath: `client/${IDclient}/vehicules` });
        const resultArray = Object.values(results);
        //console.log(resultArray)
        setSelectedDataRow((prev) => {
            return {
                ...prev,
                vehicules: resultArray,
            } as IGestionClient
        });
    }, [getData]);

    const _loadPermis = useCallback(async (IDclient: Guid) => {
        const results = await getData({ customPath: `client/${IDclient}/permis` });
        const resultArray = Object.values(results);
        setSelectedDataRow((prev) => {
            return {
                ...prev,
                permis: resultArray,
            } as IGestionClient
        });
    }, [getData]);

    const _loadPaiement = useCallback(async (IDclient: Guid) => {
        const resultPaiement = await getData({ customPath: `client/${IDclient}/paiementsHistorique` });
        setDataPaiement(resultPaiement as any[]);
    }, [getData]);

    const _loadBonsDeTravail = useCallback(async (IDclient: Guid) => {
        const result = await getData({ customPath: `client/${IDclient}/bonsTravailHistorique` });
        setDataBonsDeTravail(result as any[]);
    }, [getData]);

    const _loadEstimations = useCallback(async (IDclient: Guid) => {
        const result = await getData({ customPath: `client/${IDclient}/estimationsHistorique` });
        setDataEstimations(result as any[]);
    }, [getData]);

    const _loadContrats = useCallback(async (IDclient: Guid) => {
        const result = await getData({ customPath: `client/${IDclient}/contratsHistorique` });
        setDataContrats(result as any[]);
    }, [getData]);

    const _loadRemisage = useCallback(async (IDclient: Guid) => {
        const result = await getData({ customPath: `client/${IDclient}/remisagesHistorique` });
        setDataRemisage(result as any[]);
    }, [getData]);

    const _loadData = useCallback(async (IDclient: Guid) => {
        if (isModeSelection) return;

        await Promise.all([
            _loadReservation(IDclient),
            _loadFacture(IDclient),
            _loadPaiement(IDclient),
            _loadBonsDeTravail(IDclient),
            _loadEstimations(IDclient),
            _loadContrats(IDclient),
            _loadRemisage(IDclient),
            _loadVehicules(IDclient),
            _loadPermis(IDclient)
        ]);
    }, [_loadReservation, _loadFacture, _loadPaiement, _loadBonsDeTravail, _loadEstimations, _loadContrats, _loadRemisage, _loadVehicules, isModeSelection]);

    const cellPrepared = useCallback((e: any) => {
        if (e.rowType === "data") {
            const couleurEtatBackground = e.column.dataField
            if (e.column.dataField === "idEtatReservation") {
                e.cellElement.style.cssText = `color: ${couleurEtatBackground}; background-color: ${couleurEtatBackground}`
                // e.cellElement.style.cssText = "color: blue; background-color: red";
            }
        }
    }, [])

    const onCellClick = useCallback((e: any) => {
        // const _index = Data.findIndex(

        // );
        if (e?.data?.idVehicule && e.data.noPlaque && selectedClient?.idClient) {
            // const resultArray = Object.values(e.row.data);
            const newElement = {
                idClient: selectedClient.idClient,
                idVehicle: e.data.idVehicule,
                noPlaque: e.data.noPlaque
            }
            setSelectedDataRow((prev) => {
                return {
                    ...prev,
                    vehicules: [...prev?.vehicules || [], newElement],
                } as IGestionClient
            });
            setIsFieldModifier(true);
        }
    }, [selectedClient.idClient])

    const onCellClickPermis = useCallback((e: any) => {
        if (e?.data?.idClientPermis && selectedClient?.idClient) {
            const newElement = {
                idClient: selectedClient.idClient,
                idClientPermis: e.data.idClientPermis,
                annee: e.data.annee,
                idPermisType: e.data.idPermisType,
                numero: e.data.numero
            }

            setSelectedDataRow((prev) => {
                // Check if the element already exists
                const exists = prev?.permis?.some(permis =>
                    permis.idClientPermis?.equals(newElement.idClientPermis)
                );

                if (exists) {
                    return prev; // Return unchanged if already exists
                }

                return {
                    ...prev,
                    permis: [...prev?.permis || [], newElement],
                } as IGestionClient
            });

            setIsFieldModifier(true);
        }
    }, [selectedClient.idClient])

    useEffect(() => {
        if (setSelectedValue && selectedClient) {
            setSelectedValue(selectedClient)
        }
    }, [selectedClient?.idClient]);

    const setDataRow = useCallback((e: string) => {
        setSelectedDataRow((prev) => (
            { ...prev, [e]: Guid.empty.toString() }
        ));
        setIsFieldModifier(true);
    }, [])

    const onSelectionchanged = useCallback((item: any, e: string) => {
        setIsFieldModifier(true);
        setSelectedDataRow((prev) => {
            return { ...prev, [e]: item.id }
        })
    }, [])

    const onRowDoubleClickListe = useCallback((e: any) => {
        if (setSelectedValue && e.data.idClient)
            setSelectedValue(e.data.idClient, true)
    }, [setSelectedValue]);

    const togglePopupPhoto = useCallback(() => {
        setPopupVisibilityPhoto(!isPopupVisiblePhoto);
    }, []);

    const sauvegarderOptions = useMemo(() => ({ disabled: !isFieldModifier }), [isFieldModifier]);
    const annulerOptions = useMemo(() => ({ disabled: !isFieldModifier }), [isFieldModifier]);
    const headerText = useMemo(() => `${selectedClient?.nom || ''} ${selectedClient?.prenom || ''}`, [selectedClient?.nom, selectedClient?.prenom]);
    const dupliquerOptions = useMemo(() => ({ icon: 'copy', text: "Dupliquer" }), []);
    const carteMembreoptions = useMemo(() => ({ icon: 'print', text: "Carte Membre" }), []);
    const ficheClientoptions = useMemo(() => ({ icon: 'print', text: "Fiche client", onClick: () => setFicheClientActive(true) }), []);
    const photoOptions = useMemo(() => ({ icon: 'photo', text: "Prendre photo", onClick: () => togglePopupPhoto() }), []);
    const ecartPavOptions = useMemo(() => ({ text: "Detection des écarts profile pav" }), []);
    const rfidOptions = useMemo(() => ({ icon: 'tags', text: "Tag RIFD" }), []);
    const onFocusedRowChanged = useCallback((e: FocusedRowChangedEvent) => {
        if (isModeSelection && setSelectedValue && e.row?.data.idClient)
            setSelectedValue(e.row?.data.idClient, false)
    }, [isModeSelection, setSelectedValue]);

    const onFieldDataChanged = useCallback(() => setIsFieldModifier(true), []);

    const clientPlaqueOnInitNewRow = useCallback((e: InitNewRowEvent<IClientPlaque>) => {
        e.promise = getData<IClientPlaque>({ customPath: "clientplaque/default", returnOnly: true }).then(result => {
            e.data = result
        })
        setIsFieldModifier(true)
    }, [getData])


    const ClientPlaqueOnRowRemoving = useCallback((e: RowRemovingEvent<IClientPlaque>) => {
        e.cancel = true;
        e.data.dateSuppression = new Date();
        e.data.isDeleted = true;
        setIsFieldModifier(true);
        clientPlaqueDataGridRef.current?.instance()?.refresh()
    }, [clientPlaqueDataGridRef.current]);

    const ClientPermisOnRowRemoving = useCallback((e: RowRemovingEvent<IClientPermis>) => {
        e.cancel = true;
        e.data.dateSuppression = new Date();
        e.data.isDeleted = true;
        setIsFieldModifier(true)
        clientPermisDataGridRef.current?.instance()?.refresh()
    }, [clientPermisDataGridRef.current]);

    const filterDeletedDataGridFilterValue = ["isDeleted", "=", false];
    const clientPlaqueDataGridAjouterOptions = useMemo(() => ({ name: "add", icon: "add", text: "Ajouter", onClick: () => clientPlaqueDataGridRef?.current?.instance().addRow() }), []);
    const clientPermisDataGridAjouterOptions = useMemo(() => ({ name: "add", icon: "add", text: "Ajouter", onClick: () => clientPermisDataGridRef?.current?.instance().addRow() }), []);

    const permisDataGridOnInitNewRow = useCallback((e: InitNewRowEvent<IClientPermis>) => {
        e.promise = getData<IClientPermis>({ customPath: "clientpermis/default", returnOnly: true }).then(result => {
            e.data = result
        })
        setIsFieldModifier(true)
    }, [getData])

    return (<>
        <FormListBase key={"frm-client"} formName={"frm-client"} data={clientFormDataSource} modeSelection={Boolean(isModeSelection && !allowEditInSelectionPopup)}>
            <FormListBaseGrid ref={dataGridRef} onRowDoubleClick={onRowDoubleClickListe} onFocusedRowChanged={onFocusedRowChanged} defaultSearch={mode !== Mode.Ajout ? defaultSearch : ""}>
                <Column key={"grid-col-nom"} dataType={"string"} dataField={"nom"} caption="Nom" allowHiding={false} />
                <Column key={"grid-col-prenom"} dataType={"string"} dataField={"prenom"} caption="Prénom" allowHiding={false} />
                <Column key={"grid-col-province"} dataType={"object"} dataField={"idProvince"} caption="Province" allowHiding={false} allowSearch={false}>
                    <Lookup valueExpr={"idProvince"} displayExpr={"description"} dataSource={provinces} />
                </Column>
                <Column key={"grid-col-adresse"} dataType={"string"} dataField={"adresse"} caption="Adresse" allowHiding={false} allowSearch={false} />
                <Column key={"grid-col-ville"} dataType={"string"} dataField={"ville"} caption="Ville" allowHiding={false} allowSearch={false} />
                <Column key={"grid-col-code-postal"} dataType={"string"} dataField={"codePostal"} caption="Code postal" allowHiding={false} allowSearch={false} />
                <Column key={"grid-col-telephone"} dataType={"string"} dataField={"telephone1"} caption="Téléphone" allowHiding={false} allowSearch={false} />
                <Column key={"grid-col-categorie-client"} dataType={"object"} dataField={"idCategorieClient"} caption="Catégorie" allowHiding={false} allowSearch={false}>
                    <Lookup valueExpr={"id"} displayExpr={"description"} dataSource={categoriesClient} />
                </Column>
            </FormListBaseGrid>
            <FormListBaseToolbar headerText={headerText} sauvegarderOptions={sauvegarderOptions} annulerOptions={annulerOptions}>
                <Item key={"toolbar-item-dupliquer"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={dupliquerOptions} />
                <Item key={"toolbar-item-carte-membre"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={carteMembreoptions} />
                <Item key={"toolbar-item-fiche-client"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={ficheClientoptions} />
                <Item key={"toolbar-item-photo"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={photoOptions} />
                <Item key={"toolbar-item-ecart-pav"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={ecartPavOptions} />
                <Item key={"toolbar-item-rfid"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={rfidOptions} />
            </FormListBaseToolbar>
            <FormListBaseTab>
                <PopupGestionCategoriesClient 
                  onSelectionchanged={(data: any) => {
                    setIsFieldModifier(true);
                    formRef.current?.instance().updateData("idCategorieClient", data.idCategorieClient);
                  }}
                >
                    <GestionCategoriesClients isModeSelection={true} />
                </PopupGestionCategoriesClient>
                <PopupGestionClientTitre 
                  onSelectionchanged={(data: any) => {
                    setIsFieldModifier(true);
                    formRef.current?.instance().updateData("idClientTitre", data.idClientTitre);
                  }}
                >
                    <ConfigurationClientsTitres isModeSelection={true} />
                </PopupGestionClientTitre>
                <PopupGestionLangues 
                  onSelectionchanged={(data: any) => {
                    setIsFieldModifier(true);
                    formRef.current?.instance().updateData("idLangue", data.idLangue);
                  }}
                >
                    <GestionLangues isModeSelection={true} />
                </PopupGestionLangues>
                <PopupGestionPublicite 
                  onSelectionchanged={(data: any) => {
                    setIsFieldModifier(true);
                    formRef.current?.instance().updateData("idPublicite", data.idPublicite);
                  }}    
                >
                    <GestionPublicite isModeSelection={true} />
                </PopupGestionPublicite>
                <PopupGestionTypeSejour 
                onSelectionchanged={(data: any) => {
                    setIsFieldModifier(true);
                    formRef.current?.instance().updateData("idTypeSejour", data.idTypeSejour);
                  }}
                >
                    <GestionTypesSejours isModeSelection={true} />
                </PopupGestionTypeSejour>
                <PopupGestionLieuSejour 
                  onSelectionchanged={(data: any) => {
                    setIsFieldModifier(true);
                    formRef.current?.instance().updateData("idLieuSejour", data.idLieuSejour);
                  }}
                >
                    <GestionLieuxSejour isModeSelection={true} />
                </PopupGestionLieuSejour>
                <PopupGestionEntreprise 
                onSelectionchanged={(data: any) => {
                    setIsFieldModifier(true);
                    formRef.current?.instance().updateData("idEntreprise", data.idEntreprise);
                  }}
                >
                    <GestionEntreprises isModeSelection={true} />
                </PopupGestionEntreprise>
                <PopupGestionPlan 
                onSelectionchanged={(data: any) => {
                    setIsFieldModifier(true);
                    formRef.current?.instance().updateData("idPlanEntete", data.idPlanEntete);
                  }}
                >
                    <GestionPlans isModeSelection={true} />
                </PopupGestionPlan>

                <TabItem title="Général">
                    <Form ref={formRef} readOnly={mode === Mode.Lecture} formData={selectedClient || {} as IClient} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
                        <GroupItem colCount={2} alignItemLabels={false}>
                            <GroupItem alignItemLabels={true}>
                                <GroupItem>
                                    <SimpleItem key={"frm-nom"} dataField={"nom"} editorOptions={nomOptions}>
                                    </SimpleItem>
                                    <SimpleItem key={"frm-prenom"} dataField={"prenom"} editorOptions={nomOptions}>
                                        <Label text={"Prénom"} />

                                    </SimpleItem>
                                    <SimpleItem key={"frm-codeClient"} dataField={"codeClient"} editorOptions={codeClientOptions}>
                                        <Label text={"Code client"} />
                                    </SimpleItem>
                                    <SimpleItem key={"frm-password"} dataField={"password"} editorOptions={passwordOptions}>
                                        <Label text={"Mot de passe"} />
                                    </SimpleItem>
                                    <SimpleItem key={"frm-adresse"} dataField={"adresse"} editorOptions={adresseOptions}>
                                        <Label text={"Adresse"} />
                                    </SimpleItem>
                                    <GroupItem colCount={2}>
                                        <SimpleItem key={"frm-codePostal"} dataField={"codePostal"} editorOptions={codePostalOptions} />
                                        <SimpleItem key={"frm-ville"} dataField={"ville"} editorOptions={nomOptions} />
                                    </GroupItem>
                                    <SimpleItem editorType={"dxSelectBox"} dataField={"idCategorieClient"} editorOptions={categoriesClientEditorOption}>
                                        <Label text={"Catégorie de client"} />
                                    </SimpleItem>
                                    <GroupItem colCount={3}>
                                        <SimpleItem colSpan={2} key={"frm-idProvince"} dataField={"idProvince"} editorType={"dxSelectBox"} render={ProvinceSelectBox}>
                                            <Label text={"Province"} />
                                        </SimpleItem>
                                        <SimpleItem key={"frm-idPays"} dataField={currentProvince?.idPays.toString()} cssClass={"pays"} editorType={"dxSelectBox"} render={PaysSelectBox}>
                                            <Label visible={false} />
                                        </SimpleItem>
                                    </GroupItem>
                                    <GroupItem colCount={2}>
                                        <SimpleItem dataField={"idClientTitre"} key={"frm-idClientTitre"} editorType={"dxSelectBox"} editorOptions={clientTitreEditorOption}>
                                            <Label text={"Titre"} />
                                        </SimpleItem>
                                        <SimpleItem dataField={"idLangue"} key={"frm-idLangue"} editorType={"dxSelectBox"} editorOptions={languesEditorOption}>
                                            <Label text={"Langue"} />
                                        </SimpleItem>
                                    </GroupItem>
                                    <SimpleItem dataField={"idPublicite"} key={"frm-idPublicite"} editorType={"dxSelectBox"} editorOptions={publicitesEditorOption}>
                                        <Label text={"Publicité"} />
                                    </SimpleItem>
                                    <GroupItem colCount={2}>
                                        <SimpleItem key={"frm-dateNaissance"} dataField={"dateNaissance"} editorType={"dxDateBox"} editorOptions={dateOptions}>
                                            <Label text={"Date de naissance"} />
                                        </SimpleItem>
                                        <SimpleItem dataField={"sexe"} key={"frm-sexe"} editorType={"dxSelectBox"} render={SexeSelectBox}>
                                            <Label text={"sexe"} />
                                        </SimpleItem>
                                    </GroupItem>
                                    <SimpleItem dataField={"idTypeSejour"} key={"frm-idTypeSejour"} editorType={"dxSelectBox"} editorOptions={typeSejourEditorOption}>
                                        <Label text={"Type de séjour"} />
                                    </SimpleItem>
                                    <SimpleItem dataField={"idLieuSejour"} key={"frm-idLieuSejour"} editorType={"dxSelectBox"} editorOptions={lieuSejourEditorOption}>
                                        <Label text={"Lieu de séjour"} />
                                    </SimpleItem>
                                    <GroupItem colCount={3}>
                                        <SimpleItem colSpan={2} key={"frm-noChasseur"} dataField={"noChasseur"} editorOptions={noChasseurOptions}>
                                            <Label text='Numéro de chasseur' />
                                        </SimpleItem>
                                        <SimpleItem key={"frm-etudiantSaison"} dataField={"etudiantSaison"}>
                                            <Label text='Étudiant saison' />
                                        </SimpleItem>
                                    </GroupItem>
                                    <SimpleItem key={"frm-typeResidence"} dataField={"typeResidence"}>
                                        <Label text='Type de résidence' />
                                    </SimpleItem>
                                </GroupItem>
                                <GroupItem colCount={4} colSpan={3} alignItemLabels={false}>
                                    <SimpleItem colSpan={2} key={"frm-carteCredit"} dataField={"carteCredit"} editorOptions={carteCreditOptions}>
                                        <Label text='Carte de crédit' />
                                    </SimpleItem>
                                    <SimpleItem key={"frm-carteCreditExp"} dataField={"carteCreditExp"} editorOptions={carteCreditExpOptions}>
                                        <Label text={"Exp."} />
                                    </SimpleItem>
                                    <SimpleItem key={"frm-carteCreditVcc"} dataField={"carteCreditVcc"} editorOptions={carteCreditExpOptions}>
                                        <Label text={"VCC"} />
                                    </SimpleItem>
                                </GroupItem>
                                <TabbedItem>
                                    <TabPanelOptions height={"100%"} />
                                    <Tab title={"Notes"}>
                                        <FormItem key={"frm-notes"} dataField={"note"} editorType={"dxTextArea"}>
                                            <Label visible={false} />
                                        </FormItem>
                                    </Tab>
                                    <Tab title={"Allergies"}>
                                        <FormItem key={"frm-allergies"} dataField={"allergies"} editorType={"dxTextArea"}>
                                            <Label visible={false} />
                                        </FormItem>
                                    </Tab>
                                    <Tab title={"Vehicules"}>
                                        <FormItem key={"frm-vehicules"}>
                                            <Label visible={false} />
                                            <DataGrid
                                                ref={clientPlaqueDataGridRef}
                                                keyExpr={"idClientPlaque"}
                                                showBorders={true}
                                                dataSource={selectedClient.vehicules}
                                                onRowInserted={onCellClick}
                                                focusedRowEnabled={true}
                                                defaultFocusedRowIndex={0}
                                                autoNavigateToFocusedRow={true}
                                                height={"100%"}
                                                noDataText={"Aucun véhicule(s)"}
                                                onInitNewRow={clientPlaqueOnInitNewRow}
                                                filterValue={filterDeletedDataGridFilterValue}
                                                onRowRemoving={ClientPlaqueOnRowRemoving}
                                            >
                                                <GridToolbar visible={mode !== Mode.Lecture}>
                                                    <GridToolbarItem widget={"dxButton"} locateInMenu={"auto"} options={clientPlaqueDataGridAjouterOptions} />
                                                </GridToolbar>
                                                <Editing
                                                    newRowPosition={"first"}
                                                    startEditAction={"click"}
                                                    mode="cell"
                                                    useIcons={true}
                                                    selectTextOnEditStart={true}
                                                    allowAdding={mode !== Mode.Lecture}
                                                    allowDeleting={mode !== Mode.Lecture}
                                                    allowUpdating={mode !== Mode.Lecture}
                                                />
                                                <Column dataField="vehicule" dataType="string" caption={"Véhicule"} />
                                                <Column dataField="noPlaque" dataType="string" caption={"Numéro de plaque"} />
                                                <Column dataField="isDeleted" dataType="boolean" visible={false} />
                                            </DataGrid>
                                        </FormItem>
                                    </Tab>
                                    <Tab title={"Permis"}>
                                        <Item>
                                            <DataGrid
                                                ref={clientPermisDataGridRef}
                                                keyExpr="idClientPermis"
                                                dataSource={selectedClient.permis}
                                                focusedRowEnabled={true}
                                                defaultFocusedRowIndex={0}
                                                autoNavigateToFocusedRow={true}
                                                onRowInserted={onCellClickPermis}
                                                onInitNewRow={permisDataGridOnInitNewRow}
                                                filterValue={filterDeletedDataGridFilterValue}
                                                noDataText={""}
                                                onRowRemoving={ClientPermisOnRowRemoving}
                                            >
                                                <GridToolbar visible={mode !== Mode.Lecture}>
                                                    <GridToolbarItem widget={"dxButton"} locateInMenu={"auto"} options={clientPermisDataGridAjouterOptions} />
                                                </GridToolbar>
                                                <Editing
                                                    allowAdding={mode !== Mode.Lecture}
                                                    allowDeleting={mode !== Mode.Lecture}
                                                    allowUpdating={mode !== Mode.Lecture}
                                                    newRowPosition={"first"} mode={"cell"}
                                                    startEditAction={"click"}
                                                />
                                                <Selection mode={"single"} />
                                                <Scrolling mode="virtual" showScrollbar="always" />
                                                <Column dataField={"idPermisType"} dataType={"object"} caption={"Type de permis"}>
                                                    <Lookup {...permisTypeEditorOptions} />
                                                </Column>
                                                <Column dataField={"numero"} dataType={"string"} />
                                                <Column dataField={"annee"} dataType={"number"} width={65} />
                                                <Column dataField="isDeleted" dataType="boolean" visible={false} />
                                            </DataGrid>
                                        </Item>
                                    </Tab>
                                </TabbedItem>
                            </GroupItem>

                            <GroupItem>
                                <GroupItem caption={"Entreprise"}>
                                    <SimpleItem
                                        key={"frm-idEntreprise"}
                                        dataField={"idEntreprise"}
                                        editorType={"dxSelectBox"}
                                        editorOptions={entrepriseEditorOption}
                                    >
                                        <Label text="Entreprise" />
                                    </SimpleItem>
                                </GroupItem>
                                <GroupItem caption={"Contact"}>
                                    <GroupItem colCount={2}>
                                        <GroupItem>
                                            <SimpleItem key={"frm-telephone1"} dataField={"telephone1"} editorOptions={telephoneOptions}>
                                                <Label text={"Téléphone 1"} />
                                            </SimpleItem>
                                            <SimpleItem key={"frm-telephone2"} dataField={"telephone2"} editorOptions={telephoneOptions}>
                                                <Label text={"Téléphone 2"} />
                                            </SimpleItem>
                                            <SimpleItem key={"frm-portable"} dataField={"portable"} editorOptions={telephoneOptions} />
                                            <SimpleItem key={"frm-fax"} dataField={"fax"} editorOptions={telephoneOptions} />
                                        </GroupItem>
                                        <EmptyItem />
                                    </GroupItem>
                                    <SimpleItem key={"frm-email"} dataField={"email"} editorOptions={emailOptions}>
                                        <Label text={"Courriel"} />
                                    </SimpleItem>
                                    <SimpleItem key={"frm-contact"} dataField={"contact"} editorOptions={nomOptions} />
                                    <SimpleItem key={"frm-etatDeCompte"} dataField={"envoieEtatDeCompte"} editorType={"dxRadioGroup"} editorOptions={etatCompteOptions}>
                                        <Label text={"État de comptes"} />
                                    </SimpleItem>
                                </GroupItem>
                                <GroupItem caption={"Statut"} colCount={4} alignItemLabels={false}>
                                    <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"} />
                                    <SimpleItem key={"frm-fumeur"} dataField={"fumeur"} editorType={"dxCheckBox"} />
                                    <SimpleItem key={"frm-agent"} dataField={"agent"} editorType={"dxCheckBox"} />
                                    <SimpleItem key={"frm-blacklist"} dataField={"blacklist"} editorType={"dxCheckBox"}>
                                        <Label text={"Liste noire"} />
                                    </SimpleItem>
                                </GroupItem>
                                <GroupItem alignItemLabels={false}>
                                    <SimpleItem key={"frm-forcerPopupNote"} dataField={"forcerPopupNote"} editorType={"dxCheckBox"}>
                                        <Label text={"Forcer l'affichage de la note sur l'utilisation du client"} />
                                    </SimpleItem>
                                    <SimpleItem key={"frm-transfertNoteReservation"} dataField={"transfertNoteReservation"} editorType={"dxCheckBox"}>
                                        <Label text={"Transférer la note du client dans les réservations"} />
                                    </SimpleItem>
                                </GroupItem>
                                <GroupItem caption={"Facturation"}>
                                    <GroupItem colCount={3}>
                                        <SimpleItem key={"frm-paieTps"} dataField={"paieTps"} editorType={"dxCheckBox"}>
                                            <Label text={"Paie la TPS"} />
                                        </SimpleItem>
                                        <SimpleItem key={"frm-paieTvq"} dataField={"paieTvq"} editorType={"dxCheckBox"}>
                                            <Label text={"Paie la TVQ"} />
                                        </SimpleItem>
                                        <SimpleItem key={"frm-hebergement"} dataField={"hebergement"} editorType={"dxCheckBox"}>
                                            <Label text={"Paie la taxe d'hébergement"} />
                                        </SimpleItem>
                                    </GroupItem>
                                    <SimpleItem key={"frm-idPlanEntete"} dataField={"idPlanEntete"} editorType={"dxSelectBox"} editorOptions={planParDefautEditorOption}>
                                        <Label text={"Plan par défaut"} />
                                    </SimpleItem>
                                    <GroupItem colCount={2}>
                                        <SimpleItem key={"frm-escompte"} dataField={"escompte"}>
                                            <Label text='% Escompte' />
                                        </SimpleItem>
                                        <EmptyItem />
                                    </GroupItem>
                                    <GroupItem colCount={2}>
                                        <SimpleItem key={"frm-creditAutorise"} dataField={"creditAutorise"}>
                                            <Label text='Montant de crédit autorisé' />
                                        </SimpleItem>
                                        <SimpleItem key={"frm-pasAutoriseAuCredit"} dataField={"pasAutoriseAuCredit"} editorType={"dxCheckBox"}>
                                            <Label text='Pas autorisé au crédit' />
                                        </SimpleItem>
                                    </GroupItem>
                                </GroupItem>
                                <GroupItem alignItemLabels={false}>
                                    <SimpleItem key={"frm-bloquerChargesResto"} dataField={"bloquerChargesResto"} editorType={"dxCheckBox"}>
                                        <Label text={"Ne pas permettre les charges du restaurant"} />
                                    </SimpleItem>
                                </GroupItem>
                                <GroupItem colCount={2}>
                                    <SimpleItem key={"frm-montantDu"} dataField={"montantDu"}>
                                        <Label text='Montant dû' />
                                    </SimpleItem>
                                    <EmptyItem />
                                </GroupItem>
                            </GroupItem>
                        </GroupItem>
                    </Form>
                </TabItem>
                <TabItem title="Réservation">
                    <div>
                        <DataGrid
                            keyExpr="idClient"
                            showBorders={true}
                            dataSource={DataReservation}
                            allowColumnReordering={true}
                            onCellPrepared={cellPrepared}
                        >
                            <Editing
                                mode='cell'
                                allowAdding={mode !== Mode.Lecture}
                                allowDeleting={mode !== Mode.Lecture}
                                allowUpdating={mode !== Mode.Lecture}
                            />
                            <GroupPanel visible={true} />
                            <Paging defaultPageSize={10} />
                            <Column dataField="annee" dataType="number" alignment="left" caption={"Annee"} groupIndex={0} />
                            <Column dataField="noConfirmation" dataType="string" caption={"Reservation"} groupIndex={1} />
                            <Column dataField="dateDebut" dataType="datetime" caption={"Date arrivee"} format={"yyyy-MM-dd"} />
                            <Column dataField="dateFin" dataType="datetime" caption={"Date depart"} format={"yyyy-MM-dd"} />
                            <Column dataField="idProduit" dataType="string" caption={"Unite / Produits"}>
                                <Lookup {...produitEditorOptions} />
                            </Column>
                            <Column dataField="duree" dataType="number" alignment="left" caption={"Duree"} />
                            <Column dataField="nombreAdultes" dataType="number" alignment="left" caption={"Adulte(s)"} />
                            <Column dataField="nombreEnfants" dataType="number" alignment="left" caption={"Enfants"} />
                            <Column dataField="idPlanEntete" dataType="string" caption={"Plan"}>
                                <Lookup {...planEnteteEditorOptions} />
                            </Column>
                            <Column dataField="montant" dataType="number" alignment="left" caption={"Montant"} />
                            <Column dataField="idEtatReservation" dataType="string" caption={"Etat"} cellRender={reservationCouleurGridCellItemRender}>

                            </Column>
                        </DataGrid>
                    </div>
                </TabItem>
                <TabItem title="Factures">
                    <div>
                        <DataGrid
                            showBorders={true}
                            dataSource={DataFature}
                            allowColumnReordering={true}
                            keyExpr="idFacturationEntete"
                        >
                            <Editing
                                mode='cell'
                                allowAdding={mode !== Mode.Lecture}
                                allowDeleting={mode !== Mode.Lecture}
                                allowUpdating={mode !== Mode.Lecture}
                            />
                            <GroupPanel visible={true} />
                            <Paging defaultPageSize={10} />
                            <Column dataField="annee" dataType="number" alignment="left" caption={"Annee"} groupIndex={0} />
                            <Column dataField="description" dataType="string" caption={"Description"} />
                            <Column dataField="nombreJours" dataType="number" alignment="left" caption={"Jour(s)"} />
                            <Column dataField="quantite" dataType="number" alignment="left" caption={"Qte"} />
                            <Column dataField="prix" dataType="number" alignment="left" caption={"Prix Unit"} />
                            <Column dataField="montant" dataType="number" alignment="left" caption={"Montant"} />
                            <Column dataField="noFacture" dataType="string" caption={"Facture"} groupIndex={1} />
                        </DataGrid>
                    </div>
                </TabItem>
                <TabItem title="Paiements">
                    <div>
                        <DataGrid
                            dataSource={DataPaiement}
                            keyExpr="idPaiement"
                            showBorders={true}
                            allowColumnReordering={true}
                        >
                            <Editing
                                mode='cell'
                                allowAdding={mode !== Mode.Lecture}
                                allowDeleting={mode !== Mode.Lecture}
                                allowUpdating={mode !== Mode.Lecture}
                            />
                            <GroupPanel visible={true} />
                            <Paging defaultPageSize={10} />
                            <Column dataField="annee" dataType="number" alignment="left" caption={"Annee"} groupIndex={0} />
                            <Column dataField="noFacture" dataType="string" caption={"No. Facture"} />
                            <Column dataField="reference" dataType="string" caption={"Reference"} />
                            <Column dataField="idTypePaiement" dataType="string" alignment="left" caption={"Type de Paiement"}>
                                <Lookup {...typePaiementEditorOptions} />
                            </Column>
                            <Column dataField="datePaiement" dataType="date" caption={"Date de Paiement"} format={"yyyy-MM-dd"} />
                            <Column dataField="noRecu" dataType="string" alignment="left" caption={"No. Recu"} />
                            <Column dataField="noDepot" dataType="string" alignment="left" caption={"No. depot"} />
                            <Column dataField="montant" dataType="number" alignment="left" caption={"Montant Paiement"} />
                            <Column dataField="maj" dataType="number" alignment="left" caption={"MAJ"} />
                            <Column dataField="z" dataType="string" alignment="left" caption={"Z"} />
                        </DataGrid>
                    </div>
                </TabItem>
                <TabItem title="Reçus">
                    {/* <div>
                                <DataGrid
                                  dataSource={DataRecus}
                                  keyExpr="idRecus"
                                  showBorders={true}
                                  allowColumnReordering={true}
                                >
                                  <GroupPanel visible={true} />
                                  <Paging defaultPageSize={10} />
                                  <Column dataField="annee" dataType="number" alignment="left" caption={"Annee"} groupIndex={0} />
                                  <Column dataField="noFacture" dataType="string" caption={"No. Facture"} />
                                  <Column dataField="reference" dataType="string" caption={"Reference"} />
                                  <Column dataField="idTypePaiement" dataType="string" alignment="left" caption={"Type de Paiement"} />
                                  <Column dataField="datePaiement" dataType="date" caption={"Date de Paiement"} format={"yyyy-MM-dd"} />
                                  <Column dataField="noRecu" dataType="string" alignment="left" caption={"No. Recu"} />
                                  <Column dataField="noDepot" dataType="string" alignment="left" caption={"No. depot"} />
                                  <Column dataField="montant" dataType="number" alignment="left" caption={"Montant Paiement"} />
                                  <Column dataField="maj" dataType="number" alignment="left" caption={"MAJ"} />
                                  <Column dataField="z" dataType="string" alignment="left" caption={"Z"} />
                                </DataGrid>
                              </div> */}
                </TabItem>
                <TabItem title="Bons de travail">
                    <div>
                        <DataGrid
                            showBorders={true}
                            dataSource={DataBonsDeTavail}
                            allowColumnReordering={true}
                            keyExpr="idFacturationEntete"
                        >
                            <Editing
                                mode='cell'
                                allowAdding={mode !== Mode.Lecture}
                                allowDeleting={mode !== Mode.Lecture}
                                allowUpdating={mode !== Mode.Lecture}
                            />
                            <GroupPanel visible={true} />
                            <Paging defaultPageSize={10} />
                            <Column dataField="annee" dataType="number" alignment="left" caption={"Annee"} groupIndex={0} />
                            <Column dataField="description" dataType="string" caption={"Description"} />
                            <Column dataField="nombreJours" dataType="number" alignment="left" caption={"Jour(s)"} />
                            <Column dataField="quantite" dataType="number" alignment="left" caption={"Qte"} />
                            <Column dataField="prix" dataType="number" alignment="left" caption={"Prix Unit"} />
                            <Column dataField="montant" dataType="number" alignment="left" caption={"Montant"} />
                            <Column dataField="noFacture" dataType="string" caption={"Facture"} groupIndex={1} />
                        </DataGrid>
                    </div>
                </TabItem>
                <TabItem title="Estimations">
                    <div>
                        <DataGrid
                            showBorders={true}
                            dataSource={DataEstimations}
                            allowColumnReordering={true}
                            keyExpr="idFacturationEntete"
                        >
                            <Editing
                                mode='cell'
                                allowAdding={mode !== Mode.Lecture}
                                allowDeleting={mode !== Mode.Lecture}
                                allowUpdating={mode !== Mode.Lecture}
                            />
                            <GroupPanel visible={true} />
                            <Paging defaultPageSize={10} />
                            <Column dataField="annee" dataType="number" alignment="left" caption={"Annee"} groupIndex={0} />
                            <Column dataField="description" dataType="string" caption={"Description"} />
                            <Column dataField="nombreJours" dataType="number" alignment="left" caption={"Jour(s)"} />
                            <Column dataField="quantite" dataType="number" alignment="left" caption={"Qte"} />
                            <Column dataField="prix" dataType="number" alignment="left" caption={"Prix Unit"} />
                            <Column dataField="montant" dataType="number" alignment="left" caption={"Montant"} />
                            <Column dataField="noFacture" dataType="string" caption={"Facture"} groupIndex={1} />
                        </DataGrid>
                    </div>
                </TabItem>
                <TabItem title="Contrats de vente">
                    <div>
                        <DataGrid
                            showBorders={true}
                            dataSource={DataContrats}
                            allowColumnReordering={true}
                            keyExpr="idFacturationEntete"
                        >
                            <Editing
                                mode='cell'
                                allowAdding={mode !== Mode.Lecture}
                                allowDeleting={mode !== Mode.Lecture}
                                allowUpdating={mode !== Mode.Lecture}
                            />
                            <GroupPanel visible={true} />
                            <Paging defaultPageSize={10} />
                            <Column dataField="annee" dataType="number" alignment="left" caption={"Annee"} groupIndex={0} />
                            <Column dataField="description" dataType="string" caption={"Description"} />
                            <Column dataField="nombreJours" dataType="number" alignment="left" caption={"Jour(s)"} />
                            <Column dataField="quantite" dataType="number" alignment="left" caption={"Qte"} />
                            <Column dataField="prix" dataType="number" alignment="left" caption={"Prix Unit"} />
                            <Column dataField="montant" dataType="number" alignment="left" caption={"Montant"} />
                            <Column dataField="noFacture" dataType="string" caption={"Facture"} groupIndex={1} />
                        </DataGrid>
                    </div>
                </TabItem>
                <TabItem title="Remisage">
                    <div>
                        <DataGrid
                            showBorders={true}
                            dataSource={DataRemisage}
                            allowColumnReordering={true}
                            keyExpr="idFacturationEntete"
                        >
                            <Editing
                                mode='cell'
                                allowAdding={mode !== Mode.Lecture}
                                allowDeleting={mode !== Mode.Lecture}
                                allowUpdating={mode !== Mode.Lecture}
                            />
                            <GroupPanel visible={true} />
                            <Paging defaultPageSize={10} />
                            <Column dataField="annee" dataType="number" alignment="left" caption={"Annee"} groupIndex={0} />
                            <Column dataField="description" dataType="string" caption={"Description"} />
                            <Column dataField="nombreJours" dataType="number" alignment="left" caption={"Jour(s)"} />
                            <Column dataField="quantite" dataType="number" alignment="left" caption={"Qte"} />
                            <Column dataField="prix" dataType="number" alignment="left" caption={"Prix Unit"} />
                            <Column dataField="montant" dataType="number" alignment="left" caption={"Montant"} />
                            <Column dataField="noFacture" dataType="string" caption={"Facture"} groupIndex={1} />
                        </DataGrid>
                    </div>
                </TabItem>
                <TabItem title="Système"><FormSysteme formData={selectedClient || {} as IClient} /></TabItem>
            </FormListBaseTab>
        </FormListBase>
        {isPopupVisiblePhoto
            ? <PopupPhoto
                // key={`${dataGridRef?.current?.instance.option("focusedRowKey")}-popup-equipement`}
                idUnite={Guid.empty}
                // idUnite={focusedRow?.idAuxiliaire || Guid.empty}
                togglePopup={togglePopupPhoto}
                // equipement={focusedRow?.reservationDetailEquipements}
                onValueChanged={
                    () => {
                        setIsFieldModifier(true)
                    }
                }
                isModeModifier={mode !== Mode.Lecture} />
            : null
        }

        {ficheClientActive ?
            <PopupPage
                titre="Rapport d'un client"
                isPopupOpen={ficheClientActive}
                onClose={() => setFicheClientActive(false)}
                mode={"ajout"}
                height={"78%"}
                width={"50%"}
            >
                <PopupFicheClient />
            </PopupPage> : null
        }
    </>
    )
}